<template>
  <div :class="[$style.base, { [$style.withImagePreview]: withImagePreview }]">
    <div v-if="withImagePreview">
      <BaseImage
        v-if="image"
        :cloudinaryId="image"
        :width="120"
        :height="120"
      />
      <div
        v-else
        :class="$style.placeholder"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="100"
          height="100"
          fill="none"
          viewBox="0 0 24 24"
        >
          <path
            :class="$style.placeholderIcon"
            fill-rule="evenodd"
            d="M8 10a1.5 1.5 0 1 0-.001-3.001A1.5 1.5 0 0 0 8 10Zm10 9H6.561l7.005-5.845c.246-.209.692-.208.933-.001L19 16.994V18a1 1 0 0 1-1 1ZM6 5h12a1 1 0 0 1 1 1v8.364l-3.203-2.732c-.99-.842-2.539-.842-3.52-.006L5 17.698V6a1 1 0 0 1 1-1Zm12-2H6C4.346 3 3 4.346 3 6v12c0 1.654 1.346 3 3 3h12c1.654 0 3-1.346 3-3V6c0-1.654-1.346-3-3-3Z"
            clip-rule="evenodd"
          />
          <mask
            id="a"
            width="18"
            height="18"
            x="3"
            y="3"
            maskUnits="userSpaceOnUse"
            style="mask-type: luminance"
          >
            <path
              fill="#fff"
              fill-rule="evenodd"
              d="M8 10a1.5 1.5 0 1 0-.001-3.001A1.5 1.5 0 0 0 8 10Zm10 9H6.561l7.005-5.845c.246-.209.692-.208.933-.001L19 16.994V18a1 1 0 0 1-1 1ZM6 5h12a1 1 0 0 1 1 1v8.364l-3.203-2.732c-.99-.842-2.539-.842-3.52-.006L5 17.698V6a1 1 0 0 1 1-1Zm12-2H6C4.346 3 3 4.346 3 6v12c0 1.654 1.346 3 3 3h12c1.654 0 3-1.346 3-3V6c0-1.654-1.346-3-3-3Z"
              clip-rule="evenodd"
            />
          </mask>
          <g mask="url(#a)">
            <path d="M0 0h24v24H0z" />
          </g>
        </svg>
      </div>
    </div>
    <div :class="$style.buttons">
      <BaseButton
        :loading="isLoading"
        color="inverted"
        @click="clickUpload"
        v-test="'_base-upload-upload'"
      >
        <div :class="$style.inner">
          <BaseIcon
            size="s"
            name="cloud-upload"
          />
          <BaseText v-test="'uploadText'">{{ innerText }}</BaseText>
        </div>
      </BaseButton>
      <BaseButton
        v-if="withDelete && existingImage"
        color="inverted"
        @click="$emit('delete')"
        v-test="'_base-upload-delete'"
      >
        {{ $t('global.actions.delete') }}
      </BaseButton>
    </div>
    <input
      id="base-upload-file"
      ref="upload"
      :class="$style.fileInput"
      type="file"
      accept="image/*,application/pdf"
      :multiple="multiupload || undefined"
      @change="onChange"
      v-test="'_base-upload-input'"
    >
    <Dropfield
      v-if="dropfield && !device.mobile && !device.tablet"
      :loading="isLoading"
      @dropped="onDrop"
      v-test="'dropfield'"
    />
    <BaseText
      v-if="showSupport"
      size="s"
      v-test="'showSupport'"
    >
      {{ $t('global.image_support') }}
    </BaseText>
    <BaseText
      v-if="showFileSizeError"
      color="error"
    >
      {{ $t('global.file_too_large', { size: '10mb' }) }}
    </BaseText>
    <BaseText
      v-if="showOrientationError"
      color="error"
      v-test="'orientationError'"
    >
      {{ $t('global.image_must_be_landscape') }}
    </BaseText>
    <BaseText
      v-if="showResolutionError"
      color="error"
      v-test="'resolutionError'"
    >
      {{ $t('global.image_resolution_too_low', { width: minWidth, height: minHeight }) }}
    </BaseText>
  </div>
</template>

<script lang="ts">
import axios from 'axios';
import gql from 'graphql-tag';
import { defineComponent } from 'vue';
import config from '@/config';
import dayjs from '@/dayjs';
import { device } from '@/user-context';
import Dropfield from './Dropfield.vue';

export type CloudinaryResponse = {
  access_mode: string;
  asset_id: string;
  bytes: number;
  created_at: string;
  etag: string;
  filename: string;
  folder: string;
  format: string;
  height: number;
  placeholder: boolean;
  public_id: string;
  resource_type: string;
  secure_url: string;
  signature: string;
  tags: string[];
  type: string;
  url: string;
  version: number;
  version_id: string;
  width: number;
};

export default defineComponent({
  components: {
    Dropfield,
  },
  inheritAttrs: false,
  props: {
    hasImage: {
      type: Boolean,
      default: false,
    },
    preset: {
      type: String,
      required: true,
    },
    showSupport: {
      type: Boolean,
      default: false,
    },
    multiupload: {
      type: Boolean,
      default: false,
    },
    dropfield: {
      type: Boolean,
      default: false,
    },
    text: {
      type: String,
    },
    useNewBackend: {
      type: Boolean,
      default: false,
    },
    withImagePreview: {
      type: Boolean,
      default: false,
    },
    withDelete: {
      type: Boolean,
      default: false,
    },
    image: {
      type: String,
    },
    enforceLandscape: {
      type: Boolean,
      default: false,
    },
    minWidth: {
      type: Number,
      default: 0,
    },
    minHeight: {
      type: Number,
      default: 0,
    },
  },
  emits: ['uploadResults', 'error', 'imageUpload', 'delete'],
  data() {
    return {
      loadingAmount: 0,
      results: null as CloudinaryResponse | any | null,
      files: null as FileList | null,
      filesSelected: 0,
      fileContents: null as string | ArrayBuffer | null,
      formData: null as
        | FormData
        | { upload_preset: string; file: string }
        | null,
      showFileSizeError: false,
      showOrientationError: false,
      showResolutionError: false,
      device,
    };
  },
  computed: {
    isLoading() {
      return this.loadingAmount > 0;
    },
    innerText() {
      if (this.text) {
        return this.text;
      }

      return this.existingImage
        ? this.$t('global.actions.edit')
        : this.$t('global.actions.attach_file');
    },
    existingImage() {
      return this.hasImage || this.image;
    },
  },
  methods: {
    clickUpload() {
      (this.$refs.upload as HTMLInputElement).click();
    },
    onDrop(files: FileList) {
      this.$emit('imageUpload', 'drop');
      this.files = files;
      this.filesSelected = this.files?.length ?? 0;
      if (this.filesSelected > 0) {
        this.uploadToCloudinary();
      }
    },
    onChange(event: Event) {
      this.$emit('imageUpload', 'click');
      const target = event.target as HTMLInputElement;
      this.files = target.files;
      this.filesSelected =
        this.multiupload && this.files
          ? this.files.length
          : this.files?.length
            ? 1
            : 0;
      if (this.filesSelected > 0) {
        this.uploadToCloudinary();
      }
    },
    async getImageDimensions(
      file: File,
    ): Promise<{ width: number; height: number }> {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e: ProgressEvent<FileReader>) => {
          const img = new Image();
          img.onload = () => {
            resolve({ width: img.width, height: img.height });
          };
          img.onerror = (err) => reject(err);
          if (e.target?.result && typeof e.target.result === 'string') {
            img.src = e.target.result;
          } else {
            reject(new Error('Could not read image file for dimensions.'));
          }
        };
        reader.onerror = (err) => reject(err);
        reader.readAsDataURL(file);
      });
    },
    async uploadToCloudinary() {
      this.showFileSizeError = false;
      this.showOrientationError = false;
      this.showResolutionError = false;

      if (!this.files || this.files.length === 0) return;

      for (let i = 0; i < this.filesSelected; i++) {
        const file = this.files[i];
        if (!file) continue;

        if (file.size > 10000000) {
          // 10MB limit
          this.showFileSizeError = true;
          if ((this.$refs.upload as HTMLInputElement)?.value) {
            (this.$refs.upload as HTMLInputElement).value = '';
          }
          continue;
        }

        const isImageFile = file.type.startsWith('image/');
        if (this.enforceLandscape && isImageFile) {
          try {
            this.loadingAmount++;
            const dimensions = await this.getImageDimensions(file);
            this.loadingAmount--;
            if (dimensions.width <= dimensions.height) {
              this.showOrientationError = true;
              this.$emit(
                'error',
                `File '${file.name}' must be in landscape orientation.`,
              );
              continue;
            }
            if (this.minWidth > 0 && dimensions.width < this.minWidth) {
              this.showResolutionError = true;
              this.$emit(
                'error',
                `File '${file.name}' width is less than ${this.minWidth}px.`,
              );
              continue;
            }
            if (this.minHeight > 0 && dimensions.height < this.minHeight) {
              this.showResolutionError = true;
              this.$emit(
                'error',
                `File '${file.name}' height is less than ${this.minHeight}px.`,
              );
              continue;
            }
          } catch {
            this.loadingAmount > 0 && this.loadingAmount--;
            this.showOrientationError = true;
            this.$emit(
              'error',
              `Could not determine orientation for file '${file.name}'.`,
            );
            continue;
          }
        }

        if (this.useNewBackend) {
          this.loadingAmount++;

          const checkFile = file.name.match(/[/.](gif|jpg|jpeg|png)$/i);
          if (!checkFile) {
            this.$emit('error', 'Invalid image file type for backend upload.');
            this.loadingAmount--;
            if ((this.$refs.upload as HTMLInputElement)?.value) {
              (this.$refs.upload as HTMLInputElement).value = '';
            }
            continue;
          }

          const convertBlobToBase64 = (
            blob: Blob,
          ): Promise<string | ArrayBuffer | null> =>
            new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onerror = reject;
              reader.onload = () => {
                resolve(reader.result);
              };
              reader.readAsDataURL(blob);
            });

          const base64ToBackend = (blob: Blob): Promise<string> =>
            convertBlobToBase64(blob).then((text) => {
              if (typeof text === 'string') {
                return text.slice(text.indexOf(',') + 1);
              }
              throw new Error('Could not convert file to base64 string.');
            });

          try {
            const backendReadyFile = await base64ToBackend(file);

            this.$apollo
              .mutate<any>({
                mutation: gql`
                  mutation uploadImage($input: UploadImageInput!) {
                    uploadImage(input: $input) {
                      clientMutationId
                      errors {
                        attribute
                        message
                        type
                      }
                      image {
                        bytes
                        filename
                        id
                        lastModified
                        mimeType
                        originalUrl
                        publicId
                        transformedUrl
                        usage
                      }
                    }
                  }
                `,
                variables: {
                  input: {
                    file: backendReadyFile,
                    lastModified: dayjs().toISOString(),
                    mimeType: file.type,
                    filename: file.name,
                    usage: 'NEWSLETTER_CONTENT',
                  },
                },
              })
              .then((response) => {
                this.results = response.data?.uploadImage;
                if (this.results) {
                  (this.results as any).filename = file.name;
                }
                this.$emit('uploadResults', this.results);
              })
              .catch(() => {
                this.$emit('error', 'Wrong filetype or upload issue.');
              })
              .finally(() => {
                this.loadingAmount--;
                if ((this.$refs.upload as HTMLInputElement)?.value) {
                  (this.$refs.upload as HTMLInputElement).value = '';
                }
              });
          } catch {
            this.$emit('error', 'Could not process file for upload.');
            this.loadingAmount--;
            if ((this.$refs.upload as HTMLInputElement)?.value) {
              (this.$refs.upload as HTMLInputElement).value = '';
            }
          }
          continue;
        }

        // Old Cloudinary upload logic
        const reader = new FileReader();
        reader.addEventListener(
          'load',
          async (e: ProgressEvent<FileReader>) => {
            this.fileContents = e.target?.result ?? null;
            if (typeof this.fileContents === 'string') {
              this.formData = {
                upload_preset: this.preset,
                file: this.fileContents,
              };
              const cloudinaryUploadUrl = `https://api.cloudinary.com/v1_1/${config.cloudinaryCloudName}/auto/upload`;

              const req = axios.create({
                withCredentials: false,
              });

              this.loadingAmount++;

              req
                .post(cloudinaryUploadUrl, this.formData)
                .then((response) => {
                  this.results = response.data as CloudinaryResponse;
                  if (this.results) {
                    (this.results as any).filename = file.name;
                  }
                  this.$emit('uploadResults', this.results);
                })
                .catch(() => {
                  this.$emit('error', 'Cloudinary upload failed.');
                })
                .finally(() => {
                  this.loadingAmount--;
                  if ((this.$refs.upload as HTMLInputElement)?.value) {
                    (this.$refs.upload as HTMLInputElement).value = '';
                  }
                });
            } else if (this.fileContents instanceof ArrayBuffer) {
              this.$emit('error', 'File processing error for Cloudinary.');
              this.loadingAmount > 0 && this.loadingAmount--;
              if ((this.$refs.upload as HTMLInputElement)?.value) {
                (this.$refs.upload as HTMLInputElement).value = '';
              }
            }
          },
        );
        if (file?.name) {
          reader.readAsDataURL(file);
        }
      }
    },
  },
});
</script>

<style
  lang="scss"
  module
>
.base {
  display: flex;
  flex-wrap: wrap;
  gap: $spacing;

  &.withImagePreview {
    align-items: flex-end;
  }

  &:not(.withImagePreview) {
    align-items: flex-start;
    flex-direction: column;
  }
}

.inner {
  display: flex;
  align-items: center;
  gap: $spacing * .5;
}

.fileInput {
  opacity: 0;
  z-index: -9001;
  position: absolute;
  bottom: 0;
  height: 0;
}

.placeholder {
  border: 1px solid var(--border-strong);
  height: 128px;
  width: 128px;
  border-radius: $radius;
  display: flex;
  align-items: center;
  justify-content: center;
}

.placeholderIcon {
  fill: var(--text-secondary);
}

.buttons {
  display: flex;
  gap: $spacing * .5;
  flex-wrap: wrap;
}
</style>
